import { Box, Chip, Tooltip, Typography } from '@mui/material';
import type { ColumnDef } from '../common/tableFeatures';
import { StatusChip } from '../common/StatusChip';
import { clipped, dateSortValue, dateTime, selectColumn } from '../common/tableCells';
import type { DpsEnrollmentDevice } from '@store/api/devicesApi';

/** TanStack column definitions for the DPS enrollment table. */
export const DPS_ENROLLMENT_COLUMNS: ColumnDef<DpsEnrollmentDevice>[] = [
  selectColumn<DpsEnrollmentDevice>(
    (row) => row.name || row.device_id,
    'Select all devices'
  ),
  {
    id: 'name',
    accessorFn: (row) => row.name || '',
    header: 'Device Name',
    cell: ({ row }) => (
      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
        {row.original.name || '-'}
      </Typography>
    ),
    size: 200,
  },
  {
    id: 'device_id',
    accessorKey: 'device_id',
    header: 'Device ID',
    cell: ({ row }) => clipped(row.original.device_id, 160, { mono: true }),
    size: 190,
  },
  {
    id: 'customer_name',
    accessorFn: (row) => row.customer_name || '',
    header: 'Customer',
    cell: ({ row }) => row.original.customer_name || '-',
  },
  {
    id: 'status',
    accessorFn: (row) => row.status || '',
    header: 'Device Status',
    cell: ({ row }) =>
      row.original.status ? <StatusChip status={row.original.status} type="device" /> : '-',
    size: 120,
  },
  {
    id: 'registration_id',
    accessorFn: (row) => row.registration_id || '',
    header: 'Registration ID',
    cell: ({ row }) => clipped(row.original.registration_id, 200, { mono: true }),
    size: 220,
  },
  {
    id: 'has_tpm',
    accessorFn: (row) => (row.has_tpm ? 1 : 0),
    header: 'TPM',
    cell: ({ row }) =>
      row.original.has_tpm ? (
        <Chip label="Yes" size="small" color="success" variant="filled" />
      ) : (
        <Chip label="No" size="small" color="default" variant="outlined" />
      ),
    size: 80,
  },
  {
    id: 'dps_enrollment_status',
    accessorFn: (row) => row.dps_enrollment_status || 'not_enrolled',
    header: 'Enrollment Status',
    cell: ({ row }) => (
      <StatusChip status={row.original.dps_enrollment_status || 'not_enrolled'} type="dps" />
    ),
    size: 150,
  },
  {
    id: 'dps_environment',
    accessorFn: (row) => row.dps_environment || '',
    header: 'DPS Environment',
    cell: ({ row }) => {
      const { dps_environment: env, dps_id_scope: idScope } = row.original;
      if (!env && !idScope) return '-';
      return (
        <Tooltip title={`DpsIdScope: ${idScope || 'N/A'}`}>
          <Box component="span">
            <StatusChip status={env || 'Unknown'} type="dpsEnv" />
          </Box>
        </Tooltip>
      );
    },
    size: 150,
  },
  {
    id: 'dps_assigned_hub',
    accessorFn: (row) => row.dps_assigned_hub || '',
    header: 'Assigned Hub',
    cell: ({ row }) => clipped(row.original.dps_assigned_hub, 200),
    size: 220,
  },
  {
    id: 'dps_enrolled_at',
    accessorFn: (row) => dateSortValue(row.dps_enrolled_at),
    header: 'Enrolled At',
    cell: ({ row }) => dateTime(row.original.dps_enrolled_at),
    size: 170,
  },
  {
    id: 'dps_last_error',
    accessorFn: (row) => row.dps_last_error || '',
    header: 'Last Error',
    cell: ({ row }) => clipped(row.original.dps_last_error, 240, { error: true }),
    size: 260,
  },
];
